import React, { useState } from 'react';
import { Box, Container, Typography, Grid, Button, IconButton, useTheme, List, ListItem, ListItemIcon, ListItemText, styled } from '@mui/material';

import CloseIcon from '@mui/icons-material/Close';
import SparklesIcon from '@mui/icons-material/AutoAwesome';
import DescriptionIcon from '@mui/icons-material/Description';
import FishboneAnalysis from './FishBoneAnalysis';
import WhyWhyWhyAnalysis from './WhyWhyAnalysis';
import { AccountTreeOutlined, PolylineOutlined } from '@mui/icons-material';


const GridBackground = styled(Box)(({ theme }) => ({
    position: 'absolute',
    inset: 0,
    backgroundImage: 'repeating-linear-gradient(0deg, #1E5BB8 0.5px, transparent 0.5px, transparent 100px), repeating-linear-gradient(90deg, #1E5BB8 0.5px, transparent 0.5px, transparent 100px)',
    backgroundSize: '100px 100px',
    opacity: 0.05,
}));

export default function AnalysisTools() {
    const theme = useTheme();
    const [activeTool, setActiveTool] = useState(null);

    const ToolCard = ({ toolName, icon: Icon, title, description, features, gradient, onClick, buttonText, buttonIcon: ButtonIcon }) => (
        <Box
            onClick={onClick}
            className="group"
            sx={{
                cursor: 'pointer',
                backgroundColor: 'white',
                borderRadius: 3,
                p: 4,
                transition: 'all 0.3s',
                border: '2px solid',
                borderColor: 'grey.100',
                '&:hover': {
                    boxShadow: theme.shadows[15],
                    borderColor: '#1E5BB8',
                    '& .icon-box-transition': {
                        transform: 'scale(1.1)',
                    }
                },
            }}
        >
            <Box
                className="icon-box-transition"
                sx={{
                    width: 64,
                    height: 64,
                    background: gradient,
                    borderRadius: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mb: 3,
                    boxShadow: theme.shadows[3],
                    transition: 'transform 0.3s',
                }}
            >
                <Icon sx={{ width: 32, height: 32, color: 'white' }} />
            </Box>
            <Typography variant="h5" sx={{ fontWeight: 700, color: 'grey.900', mb: 2 }}>
                {title}
            </Typography>
            <Typography sx={{ color: 'grey.600', mb: 3 }}>
                {description}
            </Typography>

            <List sx={{ mb: 3, p: 0 }}>
                {features.map((feature, index) => (
                    <ListItem key={index} disableGutters sx={{ py: 0.5 }}>
                        <ListItemIcon sx={{ minWidth: 36 }}>
                            <Box
                                sx={{
                                    width: 24,
                                    height: 24,
                                    borderRadius: '50%',
                                    backgroundColor: feature.color === 'blue' ? '#DBEAFE' : '#EEF2FF',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    flexShrink: 0,
                                }}
                            >
                                {feature.icon}
                            </Box>
                        </ListItemIcon>
                        <ListItemText primary={feature.text} primaryTypographyProps={{ fontSize: '0.875rem', color: 'grey.700' }} />
                    </ListItem>
                ))}
            </List>

            <Button
                variant="contained"
                fullWidth
                sx={{
                    background: gradient,
                    color: 'white',
                    px: 3,
                    py: 1.5,
                    borderRadius: 2,
                    fontWeight: 700,
                    textTransform: 'none',
                    boxShadow: theme.shadows[6],
                    '&:hover': {
                        boxShadow: theme.shadows[10],
                        background: gradient,
                    },
                }}
                startIcon={<ButtonIcon sx={{ width: 20, height: 20 }} />}
            >
                {buttonText}
            </Button>
        </Box>
    );

    return (
        <Box
            component="section"
            id="tools"
            sx={{
                py: { xs: 8, md: 12 },
                background: 'linear-gradient(to bottom right, #EFF6FF, white, #EEF2FF)',
                position: 'relative',
                overflow: 'hidden',
            }}
        >
            <GridBackground />

            <Container maxWidth="xl" sx={{ px: 3, position: 'relative', zIndex: 10 }}>
                <Box sx={{ textAlign: 'center', maxWidth: 680, mx: 'auto', mb: { xs: 8, md: 10 } }}>
                    <Box sx={{ display: 'inline-block', mb: 2 }}>
                        <Typography
                            component="span"
                            sx={{
                                background: 'linear-gradient(to right, #1E5BB8, #0047AB)',
                                color: 'white',
                                px: 3,
                                py: 1,
                                borderRadius: 999,
                                fontWeight: 700,
                                fontSize: '0.875rem',
                                textTransform: 'uppercase',
                                letterSpacing: 1.5,
                                boxShadow: theme.shadows[3],
                            }}
                        >
                            Interactive Analysis Tools
                        </Typography>
                    </Box>
                    <Typography
                        component="h2"
                        sx={{
                            fontSize: { xs: '2.5rem', md: '3rem' },
                            fontWeight: 700,
                            color: 'grey.900',
                            mb: 3,
                            lineHeight: 1.1,
                        }}
                    >
                        Perform Analysis &{' '}
                        <Box component="span" sx={{
                            background: 'linear-gradient(to right, #1E5BB8, #0047AB)',
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent',
                            backgroundClip: 'text',
                            textFillColor: 'transparent',
                        }}>
                            Generate Instant Reports
                        </Box>
                    </Typography>
                    <Typography
                        sx={{
                            fontSize: '1.25rem',
                            color: 'grey.600',
                        }}
                    >
                        Use powerful analysis tools directly in the platform with AI assistance and get comprehensive reports immediately
                    </Typography>
                </Box>


                {!activeTool ? (
                    <Grid container spacing={4} sx={{ maxWidth: 1024, mx: 'auto', px: { xs: 3 } }}>
                        <Grid item xs={12} md={6}>
                            <ToolCard
                                toolName="why"
                                onClick={() => setActiveTool('why')}
                                icon={PolylineOutlined}
                                title="Why-Why Analysis"
                                description="Drill down to root causes by asking 'Why?' multiple times. Perfect for systematic problem-solving and identifying underlying issues."
                                gradient="linear-gradient(to bottom right, #1E5BB8, #0047AB)"
                                features={[
                                    { text: '5 levels of "Why" analysis', color: 'blue', icon: <Typography component="span" sx={{ color: '#1E5BB8', fontWeight: 700, fontSize: '0.75rem' }}>✓</Typography> },
                                    { text: 'AI-assisted suggestions', color: 'blue', icon: <SparklesIcon sx={{ width: 12, height: 12, color: '#1E5BB8' }} /> },
                                    { text: 'Instant PDF report generation', color: 'blue', icon: <DescriptionIcon sx={{ width: 12, height: 12, color: '#1E5BB8' }} /> },
                                ]}
                                buttonText="Try Why-Why Analysis"
                                buttonIcon={PolylineOutlined}
                            />
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <ToolCard
                                toolName="fishbone"
                                onClick={() => setActiveTool('fishbone')}
                                icon={AccountTreeOutlined}
                                title="Fishbone Diagram Analysis"
                                description="Identify and categorize potential causes using the Ishikawa diagram. Analyze Man, Machine, Method, Material, Measurement, and Environment."
                                gradient="linear-gradient(to bottom right, #6366F1, #9333EA)"
                                features={[
                                    { text: '6M categorization system', color: 'indigo', icon: <Typography component="span" sx={{ color: 'indigo.600', fontWeight: 700, fontSize: '0.75rem' }}>✓</Typography> },
                                    { text: 'AI-powered cause suggestions', color: 'indigo', icon: <SparklesIcon sx={{ width: 12, height: 12, color: 'indigo.600' }} /> },
                                    { text: 'Comprehensive report export', color: 'indigo', icon: <DescriptionIcon sx={{ width: 12, height: 12, color: 'indigo.600' }} /> },
                                ]}
                                buttonText="Try Fishbone Analysis"
                                buttonIcon={AccountTreeOutlined}
                            />
                        </Grid>
                    </Grid>
                ) : (
                    <Box sx={{ maxWidth: 1024, mx: 'auto' }}>
                        <Box
                            sx={{
                                backgroundColor: 'white',
                                borderRadius: 3,
                                boxShadow: theme.shadows[15],
                                overflow: 'hidden',
                                border: '2px solid',
                                borderColor: 'grey.100',
                            }}
                        >
                            <Box
                                sx={{
                                    background: activeTool === 'why'
                                        ? 'linear-gradient(to right, #1E5BB8, #0047AB)'
                                        : 'linear-gradient(to right, #6366F1, #9333EA)',
                                    p: 2,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                }}
                            >

                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                    {activeTool === 'why' ? (
                                        <>
                                            <PolylineOutlined sx={{ width: 32, height: 32, color: 'white' }} />
                                            <Typography variant="h5" sx={{ fontWeight: 700, color: 'white' }}>Why-Why Analysis</Typography>
                                        </>
                                    ) : (
                                        <>
                                            <AccountTreeOutlined sx={{ width: 32, height: 32, color: 'white' }} />
                                            <Typography variant="h5" sx={{ fontWeight: 700, color: 'white' }}>Fishbone Diagram Analysis</Typography>
                                        </>
                                    )}
                                </Box>
                                <IconButton
                                    onClick={() => setActiveTool(null)}
                                    sx={{
                                        color: 'white',
                                        '&:hover': {
                                            backgroundColor: 'rgba(255, 255, 255, 0.2)',
                                        },
                                    }}
                                >
                                    <CloseIcon sx={{ width: 24, height: 24 }} />
                                </IconButton>
                            </Box>

                            {activeTool === 'why' && <WhyWhyWhyAnalysis />}
                            {activeTool === 'fishbone' && <FishboneAnalysis />}
                        </Box>
                    </Box>
                )}
            </Container>
        </Box>
    );
}
